/**
 * <dogbook-inside-book> — interior-spreads carousel.
 *
 * Mobile: one spread per view on a soft-grey card, mandatory scroll-snap,
 * round overlay arrows + line-dot indicators.
 * Desktop: two "page" cards per view, free horizontal scroll, triangle arrows
 * + a custom scrollbar whose thumb reflects (and drags) the scroll position.
 *
 * Navigation drives the scroll ourselves via a rAF tween only when snap is
 * mandatory (mobile) — CSS smooth-scroll fights scroll-snap:mandatory in
 * Chromium and sticks mid-slide. Without snap (desktop) native smooth scroll
 * is used. Progressive enhancement: the row scrolls natively without JS.
 */
if (!customElements.get('dogbook-inside-book')) {
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

  class DogbookInsideBook extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('[data-db-scroller]');
      if (!this.scroller) return;

      this.prevBtns = Array.from(this.querySelectorAll('[data-db-prev]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-db-next]'));
      this.dots = Array.from(this.querySelectorAll('[data-db-dot]'));
      this.scrollbar = this.querySelector('[data-db-scrollbar]');
      this.thumb = this.querySelector('[data-db-thumb]');

      this.raf = null;
      this.dragging = false;

      this.onStepPrev = () => this.step(-1);
      this.onStepNext = () => this.step(1);
      this.onScroll = () => this.update();

      this.prevBtns.forEach((b) => b.addEventListener('click', this.onStepPrev));
      this.nextBtns.forEach((b) => b.addEventListener('click', this.onStepNext));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (this.thumb && this.scrollbar) this.initDrag();

      this.update();
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.prevBtns.forEach((b) => b.removeEventListener('click', this.onStepPrev));
      this.nextBtns.forEach((b) => b.removeEventListener('click', this.onStepNext));
      this.scroller?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    get count() {
      return this.scroller.children.length;
    }

    /** Width of one item including the inter-item gap. */
    get itemStep() {
      const kids = this.scroller.children;
      if (kids.length > 1) {
        const s = kids[1].offsetLeft - kids[0].offsetLeft;
        if (s > 0) return s;
      }
      return this.scroller.clientWidth || 1;
    }

    get maxScroll() {
      return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    }

    get index() {
      return Math.round(this.scroller.scrollLeft / this.itemStep);
    }

    step(dir) {
      this.go(this.index + dir);
    }

    /** Scroll so item i sits at the start, honouring snap vs. free scroll. */
    go(i) {
      const sc = this.scroller;
      const target = clamp(i, 0, this.count - 1);
      const to = clamp(target * this.itemStep, 0, this.maxScroll);
      const snap = getComputedStyle(sc).scrollSnapType || '';

      if (snap.indexOf('mandatory') !== -1) {
        this.tweenTo(to);
      } else if (Math.abs(to - sc.scrollLeft) > 1) {
        sc.scrollTo({ left: to, behavior: 'smooth' });
      }
    }

    /** Manual tween — reliable under scroll-snap:mandatory. */
    tweenTo(to) {
      const sc = this.scroller;
      const from = sc.scrollLeft;
      if (Math.abs(to - from) < 1) return;
      if (this.raf) cancelAnimationFrame(this.raf);

      sc.style.scrollSnapType = 'none';
      const duration = 420;
      let startTs = null;
      const stepFn = (ts) => {
        if (startTs === null) startTs = ts;
        const t = Math.min(1, (ts - startTs) / duration);
        sc.scrollLeft = from + (to - from) * easeOutCubic(t);
        if (t < 1) {
          this.raf = requestAnimationFrame(stepFn);
        } else {
          this.raf = null;
          sc.style.scrollSnapType = ''; // restore snap; ended on a snap point
          this.update(); // reflect the final position in dots + arrow disabled states
        }
      };
      this.raf = requestAnimationFrame(stepFn);
    }

    /** Drag the custom scrollbar thumb (and click-to-jump on the track). */
    initDrag() {
      const sc = this.scroller;
      const bar = this.scrollbar;
      const thumb = this.thumb;
      let startX = 0;
      let startScroll = 0;

      const trackRange = () => bar.clientWidth - thumb.offsetWidth;

      const onMove = (e) => {
        if (!this.dragging) return;
        const range = trackRange();
        if (range <= 0) return;
        const delta = (e.clientX - startX) * (this.maxScroll / range);
        sc.scrollLeft = clamp(startScroll + delta, 0, this.maxScroll);
      };
      const onUp = (e) => {
        if (!this.dragging) return;
        this.dragging = false;
        sc.style.scrollSnapType = '';
        thumb.releasePointerCapture?.(e.pointerId);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };

      thumb.addEventListener('pointerdown', (e) => {
        this.dragging = true;
        startX = e.clientX;
        startScroll = sc.scrollLeft;
        sc.style.scrollSnapType = 'none';
        thumb.setPointerCapture?.(e.pointerId);
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        e.preventDefault();
      });

      bar.addEventListener('pointerdown', (e) => {
        if (e.target === thumb) return;
        const rect = bar.getBoundingClientRect();
        const p = clamp((e.clientX - rect.left) / rect.width, 0, 1);
        sc.scrollTo({ left: p * this.maxScroll, behavior: 'smooth' });
      });
    }

    /** Reflect scroll state in dots, thumb and arrow disabled states. */
    update() {
      if (this.raf) return; // ignore scroll events from our own tween
      const sc = this.scroller;
      const idx = this.index;

      this.dots.forEach((dot, i) =>
        dot.setAttribute('aria-current', i === idx ? 'true' : 'false')
      );

      if (this.thumb && sc.scrollWidth > 0) {
        const ratio = sc.clientWidth / sc.scrollWidth;
        const maxScroll = this.maxScroll;
        const pos = maxScroll > 0 ? sc.scrollLeft / maxScroll : 0;
        this.thumb.style.width = `${clamp(ratio * 100, 8, 100)}%`;
        this.thumb.style.left = `${pos * (100 - clamp(ratio * 100, 8, 100))}%`;
      }

      const atStart = sc.scrollLeft <= 1;
      const atEnd = sc.scrollLeft >= this.maxScroll - 1;
      this.prevBtns.forEach((b) => (b.disabled = atStart));
      this.nextBtns.forEach((b) => (b.disabled = atEnd));
    }
  }

  customElements.define('dogbook-inside-book', DogbookInsideBook);
}
